import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import AppLayout from '../components/AppLayout';
import { FinancialValue } from '../components/shared/FinancialValue';
import { issueTrackerApi, Issue } from '../services/issueTrackerApi';
import { aiAssistantApi, DocumentMatch, EmailDraft } from '../services/aiAssistantApi';
import {
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Send,
  FileText,
  RefreshCw,
  Ban,
  User,
  Calendar,
  ChevronRight,
  Sparkles,
  Mail,
  Link2,
  Copy,
  Wand2,
  FileSearch
} from 'lucide-react';
import { toast } from 'sonner';

// Status configuration
const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string; borderColor: string; icon: React.ElementType }> = {
  'pending_review': { label: 'Pending Review', color: 'var(--color-needs-human-text)', bgColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', icon: Clock },
  'approved': { label: 'Approved', color: 'var(--color-needs-human-text)', bgColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', icon: CheckCircle },
  'rejected': { label: 'Rejected', color: 'var(--color-text-muted)', bgColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', icon: XCircle },
  'sent_to_client': { label: 'Sent to Client', color: 'var(--color-needs-human-text)', bgColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', icon: Send },
  'resolved': { label: 'Resolved', color: 'var(--color-verified)', bgColor: 'transparent', borderColor: 'var(--color-verified)', icon: CheckCircle },
  'wont_fix': { label: "Won't Fix", color: 'var(--color-text-dim)', bgColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', icon: Ban }
};

// Priority configuration
const PRIORITY_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  'critical': { label: 'Critical', color: 'var(--color-needs-human-text)', bgColor: 'var(--color-needs-human-tint)' },
  'high': { label: 'High', color: 'var(--color-needs-human-text)', bgColor: 'var(--color-needs-human-tint)' },
  'medium': { label: 'Medium', color: 'var(--color-text-muted)', bgColor: 'var(--color-bg-elevated)' },
  'low': { label: 'Low', color: 'var(--color-text-dim)', bgColor: 'var(--color-bg-elevated)' }
};

// Issue type labels
const ISSUE_TYPE_LABELS: Record<string, string> = {
  'missing_document': 'Missing Document',
  'data_inconsistency': 'Data Inconsistency',
  'calculation_variance': 'Calculation Variance',
  'causation_unclear': 'Unclear Causation',
  'documentation_gap': 'Documentation Gap',
  'policy_coverage_question': 'Policy Coverage Question'
};

export default function IssueDetail() {
  const { issueId } = useParams<{ issueId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const [issue, setIssue] = useState<Issue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Action states
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  
  // AI Assistant states
  const [documentMatches, setDocumentMatches] = useState<DocumentMatch[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [confirmingMatch, setConfirmingMatch] = useState<string | null>(null);
  const [emailDraft, setEmailDraft] = useState<EmailDraft | null>(null);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  useEffect(() => {
    if (issueId) {
      fetchIssue();
    }
  }, [issueId]);

  const fetchIssue = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await issueTrackerApi.getIssue(issueId!);
      setIssue(data);
    } catch (err: any) {
      console.error('Failed to fetch issue:', err);
      setError(err.message || 'Failed to load issue');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!issue) return;

    try {
      setApproving(true);
      const userId = user?.id || user?.primaryEmailAddress?.emailAddress || 'unknown-user';
      // Backend requires request_text (the client-facing request); derive from the issue.
      const requestText = issue.what_would_fix || issue.description || 'Please provide the requested documentation.';
      await issueTrackerApi.approveIssue(issue.id, userId, requestText);
      await fetchIssue();
      toast.success('Issue approved successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to approve issue');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!issue || !rejectReason.trim()) return;

    try {
      setRejecting(true);
      const userId = user?.id || user?.primaryEmailAddress?.emailAddress || 'unknown-user';
      await issueTrackerApi.rejectIssue(issue.id, userId, rejectReason);
      await fetchIssue();
      setShowRejectModal(false);
      setRejectReason('');
      toast.success('Issue rejected');
    } catch (err: any) {
      toast.error(err.message || 'Failed to reject issue');
    } finally {
      setRejecting(false);
    }
  };

  // AI Assistant: Find matching documents
  const handleFindDocuments = async () => {
    if (!issue) return;
    
    try {
      setLoadingMatches(true);
      const result = await aiAssistantApi.matchDocuments(issue.id);
      setDocumentMatches(result.matches);
      if (result.matches.length === 0) {
        toast.info('No matching documents found');
      } else {
        toast.success(`Found ${result.matches.length} potential matches`);
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to find documents');
    } finally {
      setLoadingMatches(false);
    }
  };

  // AI Assistant: Confirm document match
  const handleConfirmMatch = async (documentId: string, documentName: string) => {
    if (!issue) return;
    
    try {
      setConfirmingMatch(documentId);
      const userId = user?.id || user?.primaryEmailAddress?.emailAddress || 'unknown-user';
      // Resolve the issue with the matched document (RESOLVED, not dismissed).
      await issueTrackerApi.resolveIssue(issue.id, userId, documentId);
      toast.success('Issue resolved with matched document!');
      // Refresh issue to get updated status
      fetchIssue();
      // Clear matches since issue is resolved
      setDocumentMatches([]);
    } catch (err: any) {
      toast.error(err.message || 'Failed to confirm match');
    } finally {
      setConfirmingMatch(null);
    }
  };

  // AI Assistant: Reject document match (just removes from list)
  const handleRejectMatch = (documentId: string) => {
    setDocumentMatches(prev => prev.filter(m => m.document_id !== documentId));
    toast.info('Match dismissed');
  };

  // AI Assistant: Draft client email
  const handleDraftEmail = async () => {
    if (!issue) return;
    
    try {
      setLoadingEmail(true);
      const result = await aiAssistantApi.draftEmail(
        [issue.id],
        'Client', // Would come from report data
        String(issue.report_id ?? '').slice(0, 8).toUpperCase()
      );
      setEmailDraft(result.draft);
      setShowEmailModal(true);
    } catch (err: any) {
      toast.error(err.message || 'Failed to draft email');
    } finally {
      setLoadingEmail(false);
    }
  };

  const copyEmailToClipboard = () => {
    if (!emailDraft) return;
    const fullEmail = `Subject: ${emailDraft.subject}\n\n${emailDraft.body}`;
    navigator.clipboard.writeText(fullEmail);
    toast.success('Email copied to clipboard');
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin" style={{ color: 'var(--color-brand)' }} />
          <span className="ml-2 cs-body">Loading issue...</span>
        </div>
      </AppLayout>
    );
  }

  if (error || !issue) {
    return (
      <AppLayout>
        <div className="p-6 max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/issues')}
            className="flex items-center gap-2 cs-body mb-6 transition-colors"
            style={{ color: 'var(--color-text-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-text-primary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Issues
          </button>
          <div className="rounded-lg p-6 text-center" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
            <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-needs-human-text)' }} />
            <h2 className="cs-h2 mb-2" style={{ color: 'var(--color-needs-human-text)' }}>Issue Not Found</h2>
            <p className="cs-body" style={{ color: 'var(--color-needs-human-text)' }}>{error || 'The requested issue could not be found.'}</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  const statusConfig = STATUS_CONFIG[issue.status] || STATUS_CONFIG['pending_review'];
  const priorityConfig = PRIORITY_CONFIG[issue.priority] || PRIORITY_CONFIG['medium'];
  const StatusIcon = statusConfig.icon;
  const canTakeAction = issue.status === 'pending_review';

  return (
    <AppLayout>
      <div className="p-6 max-w-4xl mx-auto" data-testid="issue-detail-page">
        {/* Back Button */}
        <button
          onClick={() => navigate('/issues')}
          className="flex items-center gap-2 cs-body mb-6 transition-colors"
          style={{ color: 'var(--color-text-muted)' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-text-primary)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; }}
          data-testid="back-to-issues-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Issues
        </button>

        {/* Header */}
        <div className="rounded-lg p-6 mb-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className="cs-chip px-3 py-1 rounded-full" style={{ background: priorityConfig.bgColor, color: priorityConfig.color }}>
                {priorityConfig.label}
              </span>
              <span className="flex items-center gap-1.5 px-3 py-1 rounded-full" style={{ background: statusConfig.bgColor, border: `1px solid ${statusConfig.borderColor}` }}>
                <span className="inline-flex" style={{ color: statusConfig.color }}>
                  <StatusIcon className="w-4 h-4" />
                </span>
                <span className="cs-chip" style={{ color: statusConfig.color }}>{statusConfig.label}</span>
              </span>
            </div>

            <div className="cs-meta" style={{ color: 'var(--color-text-dim)' }}>
              ID: {String(issue.id ?? '').slice(0, 12)}...
            </div>
          </div>

          <h1 className="cs-display mb-2">{issue.title}</h1>

          <div className="flex items-center gap-4 cs-meta">
            <span className="flex items-center gap-1">
              <FileText className="w-4 h-4" />
              {ISSUE_TYPE_LABELS[issue.issue_type] || issue.issue_type}
            </span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              Detected by {issue.detected_by}
            </span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(issue.created_at)}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <div className="rounded-lg p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <h2 className="cs-h3 mb-3">Description</h2>
              <p className="cs-body whitespace-pre-wrap">{issue.description}</p>
            </div>

            {/* What Would Fix */}
            <div className="rounded-lg p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                <h2 className="cs-h3">Recommended Resolution</h2>
              </div>
              <p className="cs-body whitespace-pre-wrap">{issue.what_would_fix}</p>
            </div>

            {/* AI Document Matches */}
            <div className="rounded-lg p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Wand2 className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                  <h2 className="cs-h3">AI Document Matcher</h2>
                </div>
                <button
                  onClick={handleFindDocuments}
                  disabled={loadingMatches}
                  className="cs-chip flex items-center gap-2 px-3 py-1.5 rounded-lg disabled:opacity-50 transition-colors"
                  style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
                  data-testid="find-documents-btn"
                >
                  {loadingMatches ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileSearch className="w-4 h-4" />
                  )}
                  Find Documents
                </button>
              </div>

              {documentMatches.length > 0 ? (
                <div className="space-y-3">
                  {documentMatches.map((match, idx) => (
                    <div key={idx} className="rounded-lg p-4" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border-sub)' }}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />
                          <span className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{match.document_name}</span>
                        </div>
                        <span
                          className="cs-chip px-2 py-0.5 rounded"
                          style={
                            match.match_confidence >= 0.8
                              ? { background: 'transparent', color: 'var(--color-verified)' }
                              : match.match_confidence >= 0.6
                              ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                              : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }
                          }
                        >
                          {(match.match_confidence * 100).toFixed(0)}% match
                        </span>
                      </div>
                      <p className="cs-meta mb-2">{match.relevance_explanation}</p>
                      <p className="cs-chip mb-3" style={{ color: 'var(--color-needs-human-text)' }}>{match.suggested_action}</p>

                      {/* Confirm/Reject buttons */}
                      <div className="flex items-center gap-2 pt-2" style={{ borderTop: '1px solid var(--color-border-faint)' }}>
                        <button
                          onClick={() => handleConfirmMatch(match.document_id, match.document_name)}
                          disabled={confirmingMatch === match.document_id}
                          className="cs-chip flex items-center gap-1.5 px-3 py-1.5 rounded-lg disabled:opacity-50 transition-colors"
                          style={{ background: 'var(--color-verified)', color: 'var(--color-on-brand)' }}
                          data-testid={`confirm-match-${idx}`}
                        >
                          {confirmingMatch === match.document_id ? (
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <CheckCircle className="w-3.5 h-3.5" />
                          )}
                          Resolve with this
                        </button>
                        <button
                          onClick={() => handleRejectMatch(match.document_id)}
                          className="cs-chip flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors"
                          style={{ color: 'var(--color-text-muted)' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                          data-testid={`reject-match-${idx}`}
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          Dismiss
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="cs-meta text-center py-4">
                  Click "Find Documents" to search for documents that might resolve this issue
                </p>
              )}
            </div>

            {/* Status History */}
            {issue.status_history && issue.status_history.length > 0 && (
              <div className="rounded-lg p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                <h2 className="cs-h3 mb-4">Status History</h2>
                <div className="space-y-4">
                  {(Array.isArray(issue.status_history) ? issue.status_history : []).map((change, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full mt-2" style={{ background: 'var(--color-text-ghost)' }} />
                      <div>
                        <div className="cs-body" style={{ color: 'var(--color-text-primary)' }}>
                          <span style={{ fontWeight: 500 }}>{change.changed_by}</span>
                          {' changed status from '}
                          <span style={{ fontWeight: 500 }}>{STATUS_CONFIG[change.from_status]?.label || change.from_status}</span>
                          {' to '}
                          <span style={{ fontWeight: 500 }}>{STATUS_CONFIG[change.to_status]?.label || change.to_status}</span>
                        </div>
                        {change.reason && (
                          <p className="cs-meta mt-1">Reason: {change.reason}</p>
                        )}
                        <p className="cs-meta mt-1" style={{ color: 'var(--color-text-dim)' }}>{formatDate(change.changed_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Resolution (if resolved) */}
            {issue.resolution && (
              <div className="rounded-lg p-6" style={{ background: 'transparent', border: '1px solid var(--color-verified)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                  <h2 className="cs-h3" style={{ color: 'var(--color-verified)' }}>Resolution</h2>
                </div>
                <p className="cs-body" style={{ color: 'var(--color-verified)' }}>{issue.resolution}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions */}
            {canTakeAction && (
              <div className="rounded-lg p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                <h2 className="cs-h3 mb-4">Actions</h2>
                <div className="space-y-3">
                  <button
                    onClick={handleApprove}
                    disabled={approving}
                    className="cs-body w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
                    data-testid="approve-issue-btn"
                  >
                    {approving ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    Approve Issue
                  </button>

                  <button
                    onClick={() => setShowRejectModal(true)}
                    className="cs-body w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg transition-colors"
                    style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-bg-surface)'; }}
                    data-testid="reject-issue-btn"
                  >
                    <XCircle className="w-4 h-4" />
                    Reject Issue
                  </button>
                </div>
              </div>
            )}

            {/* AI Email Drafter */}
            <div className="rounded-lg p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center gap-2 mb-4">
                <Mail className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                <h2 className="cs-h3">AI Email Drafter</h2>
              </div>
              <p className="cs-meta mb-4">
                Generate a professional email to request the missing documentation from the client.
              </p>
              <button
                onClick={handleDraftEmail}
                disabled={loadingEmail}
                className="cs-body w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg disabled:opacity-50 transition-colors"
                style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
                data-testid="draft-email-btn"
              >
                {loadingEmail ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Wand2 className="w-4 h-4" />
                )}
                Draft Email
              </button>
            </div>

            {/* Details */}
            <div className="rounded-lg p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <h2 className="cs-h3 mb-4">Details</h2>
              <dl className="space-y-3">
                <div>
                  <dt className="cs-label">Case ID</dt>
                  <dd className="cs-figure">{issue.report_id}</dd>
                </div>
                <div>
                  <dt className="cs-label">Affected Amount</dt>
                  <dd className="cs-figure" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>
                    <FinancialValue value={issue.affected_amount} />
                  </dd>
                </div>
                <div>
                  <dt className="cs-label">Created</dt>
                  <dd className="cs-body" style={{ color: 'var(--color-text-primary)' }}>{formatDate(issue.created_at)}</dd>
                </div>
                {issue.updated_at && (
                  <div>
                    <dt className="cs-label">Last Updated</dt>
                    <dd className="cs-body" style={{ color: 'var(--color-text-primary)' }}>{formatDate(issue.updated_at)}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Quick Links */}
            <div className="rounded-lg p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <h2 className="cs-h3 mb-4">Related</h2>
              <div className="space-y-2">
                <button
                  onClick={() => navigate(`/report/${issue.report_id}`)}
                  className="cs-body w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors"
                  style={{ color: 'var(--color-text-secondary)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span className="flex items-center gap-2">
                    <FileText className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                    View Case Report
                  </span>
                  <ChevronRight className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Reject Modal */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="rounded-lg p-6 w-full max-w-md mx-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <h3 className="cs-h2 mb-4">Reject Issue</h3>
              <p className="cs-meta mb-4">
                Please provide a reason for rejecting this issue.
              </p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason..."
                className="cs-body w-full px-3 py-2 rounded-lg resize-none"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                rows={4}
                data-testid="reject-reason-input"
              />
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectReason('');
                  }}
                  className="cs-body px-4 py-2 rounded-lg transition-colors"
                  style={{ color: 'var(--color-text-secondary)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={rejecting || !rejectReason.trim()}
                  className="cs-body px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
                  data-testid="confirm-reject-btn"
                >
                  {rejecting ? 'Rejecting...' : 'Reject Issue'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Email Draft Modal */}
        {showEmailModal && emailDraft && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="cs-h2">AI-Generated Email Draft</h3>
                <span
                  className="cs-chip px-2 py-1 rounded"
                  style={
                    emailDraft.urgency === 'HIGH'
                      ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                      : emailDraft.urgency === 'MEDIUM'
                      ? { background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }
                      : { background: 'transparent', color: 'var(--color-verified)' }
                  }
                >
                  {emailDraft.urgency} Priority
                </span>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="cs-label block mb-1">Subject</label>
                  <div className="cs-body px-3 py-2 rounded-lg" style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)', fontWeight: 500 }}>
                    {emailDraft.subject}
                  </div>
                </div>

                <div>
                  <label className="cs-label block mb-1">Body</label>
                  <div className="cs-body px-3 py-2 rounded-lg whitespace-pre-wrap" style={{ background: 'var(--color-bg-elevated)' }}>
                    {emailDraft.body}
                  </div>
                </div>

                {emailDraft.deadline_suggestion && (
                  <div className="cs-meta">
                    <span style={{ fontWeight: 500 }}>Suggested deadline:</span> {emailDraft.deadline_suggestion}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="cs-body px-4 py-2 rounded-lg transition-colors"
                  style={{ color: 'var(--color-text-secondary)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  Close
                </button>
                <button
                  onClick={copyEmailToClipboard}
                  className="cs-body flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
                  style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
                  data-testid="copy-email-btn"
                >
                  <Copy className="w-4 h-4" />
                  Copy to Clipboard
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
